import {
  useCallback,
  useEffect,
  useState,
  ReactNode,
  SyntheticEvent,
  useMemo,
  useId,
} from 'react';

import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import classNames from 'classnames';

import { useDebounce } from '../../hooks';
import { Icon } from '../../Icon';
import { useAutocompleteReducer } from '../hooks';
import { Label, Preloader } from '../common';
import { Filter } from '../types';
import { Box } from '../../Box';
import { TextFieldMasked } from '../TextFieldMasked';

import { ListBox } from './ListBox';
import useStyles from './AddressInput.styles';

/**
 * Свойства компонента {@link AddressInput}
 */
export interface AddressInputProps {
  /**
   * Атрибут ID для поля ввода.
   */
  id?: string;

  /**
   * Атрибут name для поля ввода.
   */
  name?: string;

  /**
   * Функция для загрузки элементов подсказок.
   */
  loadItems: (
    filter: Filter,
  ) => Promise<{ value: string; addressCompleteness?: number }[]>;

  /**
   * Текст метки для поля ввода.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Текущее значение поля ввода.
   */
  value: string | undefined;

  /**
   * Функция обратного вызова для обработки изменений значения ввода.
   */
  onChange: (value: string | undefined) => void;

  /**
   * Функция обратного вызова для обработки события потери фокуса.
   */
  onBlur?: () => void;

  /**
   * Текст плейсхолдера для поля ввода.
   */
  placeholder?: string;

  /**
   * Если true, поле ввода будет отключено.
   */
  disabled?: boolean;

  /**
   * Если true, поле ввода будет отмечено как обязательное.
   */
  required?: boolean;

  /**
   * Если true, отображает кнопку для быстрой очистки элемента.
   */
  clearable?: boolean;

  /**
   * Если true, поле ввода будет показывать индикатор загрузки.
   */
  loading?: boolean;

  /**
   * Содержимое всплывающей подсказки, отображаемой с меткой.
   */
  tooltip?: ReactNode | null;

  /**
   * Если true, поле ввода будет отмечено как содержащее ошибку.
   */
  error?: boolean;

  /**
   * Вспомогательный текст, отображаемый с полем ввода.
   */
  helperText?: string;

  /**
   * Текст, отображаемый, когда нет доступных вариантов.
   */
  noOptionsText?: string;

  /**
   * Пользовательская функция отображения вариантов.
   */
  renderOption?: (option: string) => ReactNode;

  /**
   * Максимальное количество элементов для запроса.
   */
  limit?: number;

  /**
   * Минимальная длина ввода, необходимая для вызова подсказок.
   */
  minInputValueLength?: number;

  /**
   * Определяет полноту заполненности адреса.
   */
  addressCompleteness?: number;

  /**
   * Маска, применяемая к полю ввода.
   */
  mask?: string;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;
}

const DEBOUNCE_TIMEOUT = 200;
const SHOW_RESULT_LIMIT = 10;

/**
 * AddressInput — компонент, который предоставляет поле ввода с подсказками.
 * Он поддерживает локальные и загружаемые подсказки, настраиваемое отображение вариантов и всплывающие подсказки.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param name - Атрибут name для поля ввода.
 * @param loadItems - Функция для загрузки элементов подсказок.
 * @param label - Текст метки для поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param value - Текущее значение поля ввода.
 * @param onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param onBlur - Функция обратного вызова для обработки события потери фокуса.
 * @param placeholder - Текст плейсхолдера для поля ввода.
 * @param disabled - Если true, поле ввода будет отключено.
 * @param required - Если true, поле ввода будет отмечено как обязательное.
 * @param clearable - Если true, отображает кнопку для быстрой очистки элемента.
 * @param loading - Если true, поле ввода будет показывать индикатор загрузки.
 * @param tooltip - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @param error - Если true, поле ввода будет отмечено как содержащее ошибку.
 * @param helperText - Вспомогательный текст, отображаемый с полем ввода.
 * @param noOptionsText - Текст, отображаемый, когда нет доступных вариантов.
 * @param renderOption - Пользовательская функция отображения вариантов.
 * @param limit - Максимальное количество элементов для запроса.
 * @param minInputValueLength - Минимальная длина ввода, необходимая для вызова подсказок.
 * @param addressCompleteness - Определяет полноту заполненности адреса.
 * @param mask - Маска, применяемая к полю ввода.
 * @param testId - Атрибут ID для тестирования.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link AddressInputProps}
 */
export const AddressInput = (props: AddressInputProps) => {
  const {
    id,
    name,
    label,
    labelIcon,
    value,
    onChange,
    onBlur,
    placeholder,
    disabled = false,
    required = false,
    loading = false,
    tooltip,
    error = false,
    helperText,
    noOptionsText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    mask,
    testId,
  } = props;

  const { items, status, hasMore, requestItems, resetItems } =
    useAutocompleteReducer<{ value: string; addressCompleteness?: number }>(
      props.loadItems,
      true,
    );

  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const options = items.map((option) => option.value);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState<boolean>(false);

  const [lastSelectedValue, setLastSelectedValue] = useState<
    string | undefined
  >(undefined);

  const debouncedInputValue = useDebounce(value, DEBOUNCE_TIMEOUT) ?? '';

  const isItemsLoading =
    isLoading ||
    loading ||
    status === 'loading' ||
    (open &&
      value !== undefined &&
      value.replace(/_+/g, '').trim().length < minInputValueLength);

  const handleChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      nextValue: string | null,
      reason: string,
    ) => {
      if (reason === 'selectOption') {
        setLastSelectedValue(nextValue ?? undefined);
      }

      onChange(nextValue ?? undefined);

      resetItems();
      setFilter({ ...filter, offset: 0, query: '' });

      setOpen(false);
    },
    [onChange, filter, resetItems],
  );

  const handleInputChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      nextValue: string | null,
      reason: string,
    ) => {
      if (reason === 'reset') return;

      if (reason === 'clear') {
        setLastSelectedValue(undefined);
      }

      onChange(nextValue ?? undefined);

      if (nextValue?.replace(/_+/g, '').trim()) {
        if (!open) {
          setOpen(true);
        }
      } else {
        setOpen(false);
      }
    },
    [onChange, open],
  );

  const handleClose = useCallback(() => {
    onBlur?.();
    if (open) setOpen(false);
  }, [onBlur, open]);

  const handleBlur = useCallback(() => {
    onChange(lastSelectedValue);
  }, [onChange, lastSelectedValue]);

  const handleOpen = useCallback(() => {
    if (value?.replace(/_+/g, '').trim()) {
      setOpen(true);
    }
  }, [value]);

  const requestNextItems = () => {
    requestItems?.(filter, 'get');
    setFilter({
      ...filter,
      offset: filter.offset + filter.limit,
    });
  };

  useEffect(() => {
    if (
      !debouncedInputValue ||
      debouncedInputValue.length < minInputValueLength
    ) {
      return;
    }

    const abortRequest = requestItems?.(
      {
        ...filter,
        offset: 0,
        query: debouncedInputValue.replace(/_+/g, '').trim(),
      },
      'search',
    );
    setFilter({
      ...filter,
      offset: filter.limit,
    });
    setLoading(false);

    return () => {
      abortRequest?.();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedInputValue]);

  useEffect(() => {
    if (!open && value) {
      handleChange(
        {} as SyntheticEvent<Element, Event>,
        value,
        'select-option',
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterOptions = (optionList: string[]) =>
    optionList.slice(0, SHOW_RESULT_LIMIT);

  return (
    <FormControl
      className={classNames(classes.addressInput, 'aqa_address_input')}
    >
      {label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
          inputId={inputId}
        />
      )}
      <Autocomplete
        data-testid={testId}
        classes={{
          paper: classes.paper,
          option: classes.option,
          ...(value
            ? {}
            : {
                clearIndicator: classes.clearIndicatorHide,
              }),
        }}
        options={isItemsLoading ? [] : options}
        filterOptions={filterOptions}
        fullWidth
        noOptionsText={noOptionsText}
        renderTags={() => null}
        disabled={disabled || loading}
        clearIcon={
          <Icon name="dismiss_substract" color="stroke.secondary" size="24" />
        }
        loading={isItemsLoading}
        loadingText={
          <Box display="flex" justifyContent="center" p="16">
            <CircularProgress size={24} />
          </Box>
        }
        inputValue={loading ? 'Загрузка...' : value}
        onInputChange={mask ? undefined : handleInputChange}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onBlur={handleBlur}
        renderOption={(renderOptionProps, option) => (
          <li {...renderOptionProps}>
            {renderOption ? renderOption(option) : <div>{option}</div>}
          </li>
        )}
        slotProps={{
          listbox: {
            component: ListBox,
            ...{
              dataLength: options.length,
              next: requestNextItems,
              hasMore,
              loading: isItemsLoading,
              long: !!renderOption,
            },
          },
        }}
        renderInput={(params) => {
          return mask ? (
            <TextFieldMasked
              {...params}
              id={inputId}
              mask={mask}
              name={name}
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              loading={loading}
              classes={{ root: classes.inputRoot }}
              onChange={handleInputChange as never}
            />
          ) : (
            <TextField
              {...params}
              id={inputId}
              name={name}
              classes={{ root: classes.inputRoot }}
              autoComplete="off"
              variant="outlined"
              fullWidth
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              {...(loading
                ? {
                    slotProps: {
                      input: {
                        endAdornment: <Preloader />,
                      },
                    },
                  }
                : {})}
            />
          );
        }}
      />
    </FormControl>
  );
};
